<template>
  <div>
    <h2 class="title is-2">
      Subscription
    </h2>

    <b-notification
      v-if="paymentFailed"
      type="is-danger"
      :closable="false"
    >
      There was a problem with your payment card: <strong>{{ paymentFailureMessage }}</strong>
      If you need to you can <router-link :to="{ name: 'card-details-update' }">update your card details</router-link>.
    </b-notification>

    <b-notification
      v-if="showCheckoutSuccess"
      type="is-success"
      :closable="true"
    >
      Thank you! Your subscription is now active and you are now able to download tracks.
    </b-notification>

    <b-notification
      v-if="showPaymentDetailsUpdated"
      type="is-success"
      :closable="true"
    >
      Thank you! Your payment details have been updated.
    </b-notification>

    <b-notification
      v-if="isCardExpiring"
      type="is-warning"
      :closable="false"
    >
      Your card is expiring soon. Please <router-link :to="{ name: 'card-details-update' }">update your card details</router-link>.
    </b-notification>

    <b-notification
      v-if="showPlanUpgradeSuccess"
      type="is-success"
      :closable="true"
    >
      You can now access Sfx! Your card will be processed for the prorated amount within 24&ndash;48 hours.
    </b-notification>

    <div
      v-if="loading"
      class="columns"
    >
      <div class="column is-12 has-text-centered">
        <br>
        <span
          class="loader xl"
          style="display: inline-block; width: 3em; height: 3em;"
        />
      </div>
    </div>

    <div class="columns">
      <div class="column is-half">
        <div
          v-if="currentSubscription && !loading"
        >
          <b-message
            :title="subscriptionTitle"
            :type="subscriptionMessageType"
            :closable="false"
          >
            <table class="table">
              <tr>
                <th>Plan</th>
                <td>{{ currentSubscription.plan.product_name }} ({{ currentSubscription.plan.amount|formattedDbAmount(null, 2, currentSubscription.plan.currency) }}/{{ currentSubscription.plan.interval }})</td>
              </tr>
              <tr>
                <th>Status</th>
                <td>{{ currentSubscription.status.charAt(0).toUpperCase() }}{{ currentSubscription.status.slice(1).replace("_", " ") }}</td>
              </tr>
              <tr>
                <th>Includes sfx</th>
                <td>{{ currentSubscription.includes_sfx|yesno }}</td>
              </tr>
              <tr>
                <th>Start date</th>
                <td>{{ currentSubscription.start_date.substring(0, 10) }}</td>
              </tr>
              <tr>
                <th>End date</th>
                <td>{{ currentSubscription.current_period_end.substring(0, 10) }}</td>
              </tr>
              <tr>
                <th>Auto-renew</th>
                <td>{{ !currentSubscription.cancel_at_period_end|yesno }}</td>
              </tr>
              <tr v-if="currentSubscription.cancel_at_period_end">
                <th>Cancelled on</th>
                <td>{{ currentSubscription.canceled_at.substring(0, 10) }}</td>
              </tr>
            </table>

            <p v-if="!currentSubscription.cancel_at_period_end">
              <a @click.prevent="showCancelModal = !showCancelModal">Cancel?</a>
            </p>
            <p v-if="currentSubscription.reactivateable">
              <a @click.prevent="showReactivateModal = !showReactivateModal">Your subscription is not set to renew. Reactivate?</a>
            </p>
          </b-message>

          <div
            v-if="currentCard && !loading"
          >
            <b-message
              title="Card details"
              :type="isCardExpiring ? 'is-warning' : 'is-success'"
              :closable="false"
            >
              <table class="table">
                <tr>
                  <th>Card</th>
                  <td>*** *** **** {{ currentCard.last4 }}</td>
                </tr>
                <tr>
                  <th>Expiry</th>
                  <td>{{ (parseInt(currentCard.exp_month) &lt; 10 ? '0' : '') + currentCard.exp_month + '/' + currentCard.exp_year }}</td>
                </tr>
              </table>

              <p>
                <router-link
                  :to="{ name: 'card-details-update' }"
                >
                  Update your card details
                </router-link>
              </p>
            </b-message>
          </div>

          <b-modal
            :active.sync="showCancelModal"
            :width="450"
          >
            <div
              class="modal-card"
              style="width: auto;"
            >
              <header
                class="modal-card-head is-block "
                style="padding: 32px 32px 8px 32px;"
              >
                <span class="icon-static xl large-trash margin-bottom-lg" />
                <h3 class="title is-3">
                  Cancel your subscription?
                </h3>
              </header>
              <section
                class="modal-card-body"
                style="padding: 12px 36px 36px 36px;"
              >
                <p>
                  Are you sure you want to cancel your subscription? Cancelling will mean you won't be able to license tracks after {{ currentSubscription.current_period_end.substring(0, 10) }}.
                </p>
              </section>
              <footer class="modal-card-foot is-block">
                <button
                  type="submit"
                  class="button is-fullwidth is-danger margin-bottom is-large margin-bottom-sm"
                  @click.prevent="handleCancellation"
                >
                  Confirm cancellation
                </button>
                <button
                  class="button is-fullwidth is-secondary is-large"
                  @click.prevent="showCancelModal = false"
                >
                  Close
                </button>
              </footer>
            </div>
          </b-modal>

          <b-modal
            :active.sync="showReactivateModal"
            :width="450"
          >
            <div
              class="modal-card"
              style="width: auto;"
            >
              <header
                class="modal-card-head is-block "
                style="padding: 32px 32px 8px 32px;"
              >
                <span class="icon-static xl small-check margin-bottom-lg" />
                <h3 class="title is-3">
                  Reactivate your subscription?
                </h3>
              </header>
              <section
                class="modal-card-body"
                style="padding: 12px 36px 36px 36px;"
              >
                <p>
                  Reactivate your subscription and continue licensing tracks?
                </p>
              </section>
              <footer class="modal-card-foot is-block">
                <button
                  type="submit"
                  class="button is-fullwidth is-primary margin-bottom is-large margin-bottom-sm"
                  @click.prevent="handleReactivation"
                >
                  Reactivate
                </button>
                <button
                  class="button is-fullwidth is-secondary is-large"
                  @click.prevent="showReactivateModal = false"
                >
                  Close
                </button>
              </footer>
            </div>
          </b-modal>
        </div>

        <div v-if="!loading && !currentSubscription">
          <p>It looks like you don't currently have a subscription.</p>
          <br>
          <p>
            <router-link
              :to="isFeatureEnabled('clean_subscribe_flow') ? { name: 'subscribe-plan' } : { name: 'pricing' }"
              style="text-decoration: underline;"
            >
              Check out our subscriptions to get unlimited access to tracks.
            </router-link>
          </p>
        </div>
      </div>

      <div
        v-if="isFeatureEnabled('sfx_plan_upgrade') && upgradeablePlans.length > 0 && !loading"
        class="column is-half"
      >
        <h3 class="title is-3">
          Upgrade your plan!
        </h3>
        <p>
          You can upgrade your subscription to get access to our new Sfx library now.
        </p>
        <br>
        <div
          v-for="plan in upgradeablePlans"
          :key="plan.id"
          class="box"
        >
          <h4 class="title is-4 mt-0">
            {{ plan.product_name }}
          </h4>
          <p>
            New price: {{ plan.amount|formattedDbAmount(null, 2, plan.currency) }}/{{ plan.interval }}
          </p>
          <div
            class="field mt-4 mb-5"
          >
            <b-checkbox v-model="hasAgreedToUpgradeTerms">
              <span class="text small">
                I understand that by upgrading my plan I will gain access to Sfx and my
                billing amount will change accordingly. I understand that my card will be charged
                the prorated amount within 24&ndash;48 hours.
              </span>
            </b-checkbox>
          </div>
          <button
            class="button is-accent is-large"
            :disabled="upgradeButtonDisabled"
            @click.prevent="handlePlanSelect(plan)"
          >
            Upgrade Now
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import paymentDetailsClient from '@/api/paymentDetails.js'
import { logUserCancelledSubscription, logUserReactivatedSubscription, logPlanUpgradeRequested } from '@/utils/segment.js'

export default {
  name: 'SubscriptionDashboard',

  props: {
    showCheckoutSuccess: {
      type: Boolean,
      required: false,
      default: false
    },
    showPaymentDetailsUpdated: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  data () {
    return {
      subscription: null,
      showCancelModal: false,
      showReactivateModal: false,
      currentCard: null,
      hasAgreedToUpgradeTerms: false,
      showPlanUpgradeSuccess: false
    }
  },

  computed: {
    ...mapGetters({ isFeatureEnabled: 'waffle/isFeatureEnabled' }),

    loading: function () {
      return this.$store.getters['subscriptions/loadingSubscription'] || this.$store.getters['user/loadingMe'] || this.$store.getters['common/loadingInitAuthenticated']
    },

    currentSubscription: function () {
      return this.$store.getters['subscriptions/currentSubscription']
    },

    subscriptionTitle: function () {
      if (this.currentSubscription) {
        if (this.currentSubscription.status === 'active') {
          return 'Active'
        } else if (this.currentSubscription.status === 'trialing') {
          return 'Active (Trialing)'
        }
        return 'Inactive'
      }
      return ''
    },

    subscriptionMessageType: function () {
      if (this.currentSubscription) {
        if (this.currentSubscription.status === 'active') {
          return 'is-info'
        } else if (this.currentSubscription.status === 'trialing') {
          // This might change.
          return 'is-info'
        } else if (this.currentSubscription.status === 'past_due') {
          return 'is-warning'
        }
      }
      return ''
    },

    isCardExpiring: function () {
      if (this.currentCard === null) {
        return false
      }
      return this.currentCard.expires_soon
    },

    paymentFailed: function () {
      if (this.currentSubscription) {
        return this.currentSubscription.payment_failed
      }
      return false
    },

    paymentFailureMessage: function () {
      if (this.currentSubscription && this.paymentFailed) {
        return this.currentSubscription.payment_failure_message
      }
      return ''
    },

    upgradeablePlans: function () {
      return this.$store.getters['subscriptions/upgradeablePlans']
    },

    selectedUpgradePlan: function () {
      return this.$store.getters['subscriptions/selectedUpgradePlan']
    },

    upgradeButtonDisabled: function () {
      return !this.hasAgreedToUpgradeTerms
    }
  },

  mounted () {
    this.$store.dispatch('waffle/fetch').then(() => {
      if (this.isFeatureEnabled('sfx_plan_upgrade')) {
        this.$store.dispatch('subscriptions/fetchUpgradeOptions')
      }
    })

    paymentDetailsClient.retrieve()
      .then((response) => {
        this.currentCard = response.data
      })
  },

  methods: {
    handleCancellation: function () {
      const vm = this
      vm.$store.dispatch('subscriptions/cancelCurrentSubscription')
        .then(() => {
          vm.showCancelModal = false
          logUserCancelledSubscription(this.currentSubscription)
        })
    },

    handleReactivation: function () {
      const vm = this
      vm.$store.dispatch('subscriptions/reactivateCurrentSubscription')
        .then(() => {
          vm.showReactivateModal = false
          logUserReactivatedSubscription(this.currentSubscription)
        })
    },

    handlePlanSelect: function (plan) {
      if (!this.isFeatureEnabled('sfx_plan_upgrade')) {
        return
      }

      const vm = this
      vm.$store.dispatch('subscriptions/selectUpgradePlan', plan)
        .then(() => {
          vm.$store.dispatch('subscriptions/createPlanChangeRequest', plan.pk)
            .then(() => {
              vm.$store.dispatch('subscriptions/fetchCurrentSubscription')
              vm.showPlanUpgradeSuccess = true
              logPlanUpgradeRequested()
            })
        })
    }
  },

  metaInfo: {
    title: 'Subscription'
  }
}
</script>

<style scoped>
  .table {
    background-color: transparent;
  }
</style>
